<template>
  <QuickStatsPageShell
    :title="title"
    :description="description"
    :status-message="statusMessage"
  >
    <div v-if="loading" class="state-text">Loading ranking detail.</div>
    <div v-else-if="!entitySummary" class="state-text">Ranking detail was not found for this entity.</div>

    <template v-else>
      <article class="card summary-card">
        <div class="summary-head">
          <img v-if="entitySummary.logoUrl" :src="entitySummary.logoUrl" :alt="`${entitySummary.entityName} logo`" class="team-logo" loading="lazy" />
          <div>
            <h2>{{ entitySummary.entityName }}</h2>
            <p class="meta">
              <QuickStatsLeagueMark :league="entitySummary.league" :logo-url="entitySummary.leagueLogo" class="league-logo" />
              {{ entitySummary.league }} · {{ formatMarketLabel(entitySummary.market) }}
            </p>
          </div>
        </div>
        <p>Latest update: {{ formatDateTime(entitySummary.updatedAt) }}</p>
        <router-link to="/quick-stats/team-rankings" class="btn btn-secondary">Back to team rankings</router-link>
      </article>

      <section class="card breakdown-card">
        <h3>Timeframe breakdown</h3>
        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Timeframe</th>
                <th class="hide-sm">Wins</th>
                <th class="hide-sm">Losses</th>
                <th class="hide-md">Pushes</th>
                <th class="hide-md">Pickems</th>
                <th>Record</th>
                <th>Win Rate</th>
                <th class="hide-md">Updated</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in prioritizedRows" :key="row.id">
                <td data-label="Timeframe">{{ row.timeframe }}</td>
                <td class="hide-sm" data-label="Wins">{{ nullable(row.wins) }}</td>
                <td class="hide-sm" data-label="Losses">{{ nullable(row.losses) }}</td>
                <td class="hide-md" data-label="Pushes">{{ nullable(row.pushes) }}</td>
                <td class="hide-md" data-label="Pickems">{{ nullable(row.pickems) }}</td>
                <td data-label="Record">{{ row.record || 'N/A' }}</td>
                <td data-label="Win Rate">{{ formatPercent(row.winRate) }}</td>
                <td class="hide-md" data-label="Updated">{{ formatDateTime(row.updatedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <p class="cross-link">
        Compare against
        <router-link to="/quick-stats/league-trends">League Trends</router-link>
        and current lines in
        <router-link to="/quick-stats/game-previews">Game Previews</router-link>.
      </p>
    </template>
  </QuickStatsPageShell>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import QuickStatsLeagueMark from '@/components/QuickStatsLeagueMark.vue'
import QuickStatsPageShell from '@/components/QuickStatsPageShell.vue'
import { formatDateTime, formatMarketLabel, formatPercent } from '@/lib/quickStats/format'
import { getTeamTrends } from '@/lib/quickStats/service'
import { buildTrendEntityKey } from '@/lib/quickStats/teamSlug'

const route = useRoute()
const loading = ref(true)
const rows = ref([])
const slugToTeam = ref(new Map())
const statusMessage = ref('')

const orderedTimeframes = ['Current Season', '3m', '1m', '7d', '1d']

const entityIdentity = computed(() => slugToTeam.value.get(route.params.teamSlug))

const entityRows = computed(() => {
  if (!entityIdentity.value) return []
  const entityKey = buildTrendEntityKey({
    category: entityIdentity.value.category,
    league: entityIdentity.value.league,
    market: entityIdentity.value.market
  })

  return rows.value.filter((row) => {
    return buildTrendEntityKey({
      category: row.entityName,
      league: row.league,
      market: row.market
    }) === entityKey
  })
})

const entitySummary = computed(() => entityRows.value[0] || null)

const prioritizedRows = computed(() => {
  const byFrame = new Map()
  entityRows.value.forEach((row) => {
    if (!byFrame.has(row.timeframe)) byFrame.set(row.timeframe, row)
  })

  const ordered = []
  orderedTimeframes.forEach((frame) => {
    const value = byFrame.get(frame)
    if (value) ordered.push(value)
  })

  byFrame.forEach((row, timeframe) => {
    if (!orderedTimeframes.includes(timeframe)) {
      ordered.push(row)
    }
  })

  return ordered
})

const title = computed(() => {
  if (!entitySummary.value) return 'Team Data'
  return `${entitySummary.value.entityName} Data`
})

const description = computed(() => {
  if (!entitySummary.value) {
    return 'Team trend breakdown by timeframe from public Paulie team trends.'
  }
  return `${entitySummary.value.entityName} ${entitySummary.value.market} trend breakdown across available timeframes.`
})

function nullable(value) {
  return value === null || value === undefined ? 'N/A' : value
}

watch(
  [title, description],
  ([nextTitle, nextDescription]) => {
    document.title = `${nextTitle} - Paulie`
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', nextDescription)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  const result = await getTeamTrends()
  rows.value = result.rows
  slugToTeam.value = result.slugToTeam
  statusMessage.value = result.errorMessage
  loading.value = false
})
</script>

<style scoped>
.state-text {
  color: var(--text-muted);
}

.summary-card,
.breakdown-card {
  padding: 1rem;
}

.breakdown-card {
  margin-top: 1rem;
}

.summary-head {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.55rem;
}

.team-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.meta {
  color: var(--text-muted);
  font-size: 0.84rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.league-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.cross-link {
  margin-top: 1rem;
}

.cross-link a {
  color: var(--accent);
  text-decoration: none;
}
</style>
